import { useEventPlaces } from '@shared/utils/eventPlace';
import { useEventInfo } from '@shared/utils/eventInfo';

/**
 * Location format options for displaying the event location
 */
export const LOCATION_FORMAT_OPTIONS = {
  /** Format: venue name, city, state or country (e.g., "The Grand Ballroom, San Francisco, CA") */
  VENUE_CITY_STATE_COUNTRY: 'venueCityStateCountry',
  /** Format: city, state or country (e.g., "San Francisco, CA") */
  CITY_STATE_COUNTRY: 'cityStateCountry',
  /** Format: city only (e.g., "San Francisco") */
  CITY_ONLY: 'cityOnly',
} as const;

export type LocationFormat = (typeof LOCATION_FORMAT_OPTIONS)[keyof typeof LOCATION_FORMAT_OPTIONS];

/**
 * Format city, state or country (e.g., "San Francisco, CA")
 */
const formatCityStateCountry = (city?: string | null, state?: string | null, country?: string | null): string => {
  return [city, state || country].filter(Boolean).join(', ');
};

/**
 * Format location according to the specified format type
 */
export const formatLocationByType = (
  location: { name?: string | null; city?: string | null; state?: string | null; country?: string | null },
  formatType: LocationFormat,
): string => {
  const { name, city, state, country } = location;

  switch (formatType) {
    case LOCATION_FORMAT_OPTIONS.VENUE_CITY_STATE_COUNTRY: {
      // venue name, city, state or country
      const cityStateCountry = formatCityStateCountry(city, state, country);
      return [name, cityStateCountry].filter(Boolean).join(', ');
    }
    case LOCATION_FORMAT_OPTIONS.CITY_STATE_COUNTRY: {
      // city, state or country
      return formatCityStateCountry(city, state, country);
    }
    case LOCATION_FORMAT_OPTIONS.CITY_ONLY: {
      // city only
      return city || '';
    }
    default:
      return name || '';
  }
};

/**
 * To determine the event's location information,
 * including a display-friendly name for use in UI.
 *
 * @param locationName - Optional override for the event location name.
 * @param locationFormat - Optional format for displaying location. When provided, formats according to the specified type.
 * @returns An object containing:
 *   - `eventLocation`: The raw event location data.
 *   - `eventLocationDisplayName`: A human-readable location name for display.
 *   - `isLoadingEventLocation`: Boolean indicating if any of the data is still loading.
 */
export const useEventLocation = (locationName?: Readonly<Maybe<string>>, locationFormat?: LocationFormat) => {
  const { eventInfo, loading: loadingEventInfo } = useEventInfo();
  const { venueInfo, loading: loadingVenueInfo } = useEventPlaces();

  const isLoadingEventLocation = loadingEventInfo || loadingVenueInfo;

  const eventLocation = eventInfo?.locationInfo;

  // If locationFormat is provided, use the format function
  if (locationFormat && eventLocation) {
    const formattedLocation = formatLocationByType(eventLocation, locationFormat);
    return { eventLocation, eventLocationDisplayName: formattedLocation || eventLocation?.name, isLoadingEventLocation };
  }

  /**
   * If the experiment is enabled AND the event location is the same as the venue,
   * we prefer to show the city name instead to avoid redundant location display.
   * Default fall back to event location name
   */
  const isSamePlace = eventLocation?.placeId && eventLocation.placeId === venueInfo?.googlePlaceId;
  const eventLocationDisplayName = isSamePlace ? eventLocation?.city || eventLocation?.name : locationName || eventLocation?.name;

  return { eventLocation, eventLocationDisplayName, isLoadingEventLocation };
};
